import React, { useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import {
  DeleteOutlined,
  EditOutlined,
  FileImageOutlined,
  KeyOutlined,
  MailOutlined,
  MoreOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  StarOutlined,
  StopOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  AdminUpdateUserInput,
  ListUsersFilter,
  UserListItemDto,
  UserRole,
  useAdjustPoints,
  useAdminUpdateUser,
  useBanUser,
  useDeleteUser,
  useRecalcAllTrustScores,
  useRecalcTrustScore,
  useUsers,
} from '@shared/api/hooks/useUsers';
import { useAdminManualReceipt } from '@shared/api/hooks/useReceipts';
import {
  SUBSCRIPTION_TIER_LABELS,
  SubscriptionTier,
  useAwardSubscription,
  useAwardTicket,
} from '@shared/api/hooks/useLoyaltyAwards';
import {
  useAdminSendPasswordReset,
  useAdminSetPassword,
  useUserProviders,
} from '@shared/api/hooks/useEmailTemplates';
import { useLoyaltyPeriods } from '@shared/api/hooks/useLoyaltyPeriods';
import type { TablePaginationConfig } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';

function trustScoreColor(score: number): string {
  if (score >= 85) return 'green';
  if (score >= 65) return 'lime';
  if (score >= 40) return 'orange';
  if (score >= 20) return 'volcano';
  return 'red';
}

const ROLE_LABEL: Record<UserRole, string> = {
  FAN: 'Tifoso',
  CLUB_EDITOR: 'Editor',
  CLUB_SHOP_MANAGER: 'Shop manager',
  CLUB_SUPPORT: 'Supporto',
  CLUB_ADMIN: 'Admin',
};

const ROLE_COLOR: Record<UserRole, string> = {
  FAN: 'blue',
  CLUB_EDITOR: 'cyan',
  CLUB_SHOP_MANAGER: 'gold',
  CLUB_SUPPORT: 'purple',
  CLUB_ADMIN: 'red',
};

interface BanFormValues {
  reason: string;
}

interface EditFormValues {
  firstName?: string;
  lastName?: string;
  email?: string;
  locale?: 'it' | 'en';
  role?: UserRole;
}

interface PointsFormValues {
  delta: number;
  reason: string;
}

interface ReceiptFormValues {
  pointsAwarded: number;
  storeName?: string;
  partitaIva?: string;
  businessName?: string;
  totalAmount?: number;
  date?: dayjs.Dayjs;
  receiptNumber?: string;
  note?: string;
}

interface LoyaltyFormValues {
  kind: 'SUBSCRIPTION' | 'TICKET';
  tier?: SubscriptionTier;
  season?: string;
  note?: string;
}

export function FansPage(): React.ReactElement {
  const [filter, setFilter] = useState<ListUsersFilter>({ page: 1, limit: 20 });
  const [search, setSearch] = useState('');
  const [banUser, setBanUser] = useState<UserListItemDto | null>(null);
  const [editUser, setEditUser] = useState<UserListItemDto | null>(null);
  const [pointsUser, setPointsUser] = useState<UserListItemDto | null>(null);
  const [receiptUser, setReceiptUser] = useState<UserListItemDto | null>(null);
  const [loyaltyUser, setLoyaltyUser] = useState<UserListItemDto | null>(null);

  const [banForm] = Form.useForm<BanFormValues>();
  const [editForm] = Form.useForm<EditFormValues>();
  const [pointsForm] = Form.useForm<PointsFormValues>();
  const [receiptForm] = Form.useForm<ReceiptFormValues>();
  const [loyaltyForm] = Form.useForm<LoyaltyFormValues>();
  const loyaltyKind = Form.useWatch('kind', loyaltyForm);

  const { data, isLoading, refetch, isFetching } = useUsers(filter);
  const { data: periods } = useLoyaltyPeriods();
  const banMutation = useBanUser();
  const updateMutation = useAdminUpdateUser();
  const pointsMutation = useAdjustPoints();
  const manualReceiptMutation = useAdminManualReceipt();
  const awardSubscriptionMutation = useAwardSubscription();
  const awardTicketMutation = useAwardTicket();
  const recalcOneMutation = useRecalcTrustScore();
  const recalcAllMutation = useRecalcAllTrustScores();

  const onSearch = (): void => {
    setFilter((prev) => ({ ...prev, page: 1, search: search || undefined }));
  };

  const handleErr = (e: unknown): string => {
    const err = e as { response?: { data?: { message?: string | string[] } } };
    const msg = err.response?.data?.message;
    return Array.isArray(msg) ? msg.join('; ') : msg ?? 'Errore';
  };

  const onSubmitBan = async (): Promise<void> => {
    if (!banUser) return;
    const v = await banForm.validateFields();
    try {
      await banMutation.mutateAsync({ id: banUser.id, reason: v.reason });
      message.success('Utente bannato');
      setBanUser(null);
      banForm.resetFields();
    } catch (e) {
      message.error(handleErr(e));
    }
  };

  const openEdit = (u: UserListItemDto): void => {
    editForm.resetFields();
    editForm.setFieldsValue({
      firstName: u.displayName?.split(' ')[0],
      lastName: u.displayName?.split(' ').slice(1).join(' ') || undefined,
      email: u.email,
      role: u.role,
    });
    setEditUser(u);
  };

  const onSubmitEdit = async (): Promise<void> => {
    if (!editUser) return;
    const v = await editForm.validateFields();
    try {
      const input: AdminUpdateUserInput = {};
      if (v.firstName) input.firstName = v.firstName;
      if (v.lastName) input.lastName = v.lastName;
      if (v.email) input.email = v.email;
      if (v.locale) input.locale = v.locale;
      if (v.role) input.role = v.role;
      await updateMutation.mutateAsync({ id: editUser.id, input });
      message.success('Tifoso aggiornato');
      setEditUser(null);
    } catch (e) {
      message.error(handleErr(e));
    }
  };

  const onSubmitPoints = async (): Promise<void> => {
    if (!pointsUser) return;
    const v = await pointsForm.validateFields();
    try {
      const res = await pointsMutation.mutateAsync({
        id: pointsUser.id,
        delta: v.delta,
        reason: v.reason,
      });
      message.success(`Punti aggiornati: ${res.totalPoints} (Δ ${v.delta >= 0 ? '+' : ''}${v.delta})`);
      setPointsUser(null);
      pointsForm.resetFields();
    } catch (e) {
      message.error(handleErr(e));
    }
  };

  const onSubmitReceipt = async (): Promise<void> => {
    if (!receiptUser) return;
    const v = await receiptForm.validateFields();
    try {
      await manualReceiptMutation.mutateAsync({
        userId: receiptUser.id,
        pointsAwarded: v.pointsAwarded,
        storeName: v.storeName,
        partitaIva: v.partitaIva?.toUpperCase(),
        businessName: v.businessName,
        totalAmount: v.totalAmount,
        date: v.date?.format('YYYY-MM-DD'),
        receiptNumber: v.receiptNumber,
        note: v.note,
      });
      message.success(`Scontrino manuale creato (+${v.pointsAwarded} pt)`);
      setReceiptUser(null);
      receiptForm.resetFields();
    } catch (e) {
      message.error(handleErr(e));
    }
  };

  const onSubmitLoyalty = async (): Promise<void> => {
    if (!loyaltyUser) return;
    const v = await loyaltyForm.validateFields();
    try {
      const res =
        v.kind === 'SUBSCRIPTION'
          ? await awardSubscriptionMutation.mutateAsync({
              userId: loyaltyUser.id,
              tier: v.tier as SubscriptionTier,
              season: v.season || undefined,
            })
          : await awardTicketMutation.mutateAsync({ userId: loyaltyUser.id, note: v.note });
      message.success(
        res.awarded
          ? `Accreditati +${res.points} pt (totale ${res.totalPoints})`
          : 'Già accreditato in precedenza (nessuna modifica)',
      );
      setLoyaltyUser(null);
      loyaltyForm.resetFields();
    } catch (e) {
      message.error(handleErr(e));
    }
  };

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Tifosi
          </Typography.Title>
          <Tag>{data?.meta.total ?? 0}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Input
            placeholder="Cerca email o nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={onSearch}
            style={{ width: 220 }}
            allowClear
            suffix={<SearchOutlined onClick={onSearch} style={{ cursor: 'pointer' }} />}
          />
          <Select<UserRole | undefined>
            placeholder="Ruolo"
            allowClear
            style={{ width: 140 }}
            value={filter.role}
            onChange={(v) => setFilter((prev) => ({ ...prev, role: v, page: 1 }))}
            options={Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label }))}
          />
          {/* T-252 — scelta della base punti: totali o una fase (periodo loyalty).
              Con una fase selezionata, la colonna "Punti" e l'ordinamento usano i
              punti di quella fase (stessa fonte della classifica). */}
          <Select<string>
            style={{ width: 190 }}
            value={filter.periodId ?? ''}
            onChange={(v) =>
              setFilter((prev) => ({ ...prev, periodId: v || undefined, page: 1 }))
            }
            options={[
              { value: '', label: 'Punti: totali' },
              ...(periods ?? []).map((p) => ({ value: p.id, label: `Punti: ${p.name}` })),
            ]}
          />
          <Button
            icon={<SafetyCertificateOutlined />}
            onClick={async () => {
              try {
                const r = await recalcAllMutation.mutateAsync();
                message.success(`Trust score ricalcolato per ${r.updated} tifosi`);
              } catch (e) {
                message.error(handleErr(e));
              }
            }}
            loading={recalcAllMutation.isPending}
          >
            Ricalcola affidabilità
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
        </Space>
      }
    >
      <Table<UserListItemDto>
        rowKey="id"
        loading={isLoading}
        dataSource={data?.data ?? []}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: filter.page,
          pageSize: filter.limit,
          total: data?.meta.total ?? 0,
          showSizeChanger: false,
        }}
        // T-252 — ordinamento e pagina gestiti dal server (prima l'ordinamento
        // era nel browser sulla sola pagina da 20 righe: riordinava una pagina,
        // non l'elenco). Il click su una colonna ordinabile imposta sortBy/order;
        // togliere l'ordinamento torna al default (piu' recenti prima).
        onChange={(
          pag: TablePaginationConfig,
          _filters,
          sorter: SorterResult<UserListItemDto> | SorterResult<UserListItemDto>[],
        ) => {
          const s = Array.isArray(sorter) ? sorter[0] : sorter;
          const key = s?.columnKey as 'points' | 'trustScore' | undefined;
          setFilter((prev) => ({
            ...prev,
            page: pag.current ?? 1,
            sortBy: s?.order && key ? key : undefined,
            order: s?.order === 'ascend' ? 'asc' : s?.order === 'descend' ? 'desc' : undefined,
          }));
        }}
        columns={[
          {
            title: '',
            width: 50,
            render: () => <Avatar icon={<UserOutlined />} />,
          },
          {
            title: 'Nome',
            // Larghezza fissa + ellissi coerenti con le altre colonne: i nomi
            // lunghi / cognomi doppi non allargano più la colonna in modo
            // incoerente. Il nome completo resta leggibile nel tooltip.
            width: 200,
            ellipsis: true,
            render: (_v, u) =>
              u.displayName ? (
                <Tooltip title={u.displayName}>
                  <span>{u.displayName}</span>
                </Tooltip>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: 'Email',
            dataIndex: 'email',
            ellipsis: true,
            responsive: ['lg'],
          },
          {
            title: 'Nickname',
            dataIndex: 'nickname',
            width: 140,
            ellipsis: true,
            responsive: ['xl'],
            render: (v: string | null) =>
              v ? (
                <Typography.Text code style={{ fontSize: 12 }}>
                  {v}
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: 'Ruolo',
            dataIndex: 'role',
            width: 130,
            responsive: ['lg'],
            render: (v: UserRole) => <Tag color={ROLE_COLOR[v]}>{ROLE_LABEL[v]}</Tag>,
          },
          {
            // Con una fase selezionata la colonna mostra i punti di quella fase.
            title: filter.periodId ? 'Punti fase' : 'Punti',
            key: 'points',
            width: 110,
            align: 'right',
            responsive: ['md'],
            sorter: true,
            sortOrder:
              filter.sortBy === 'points'
                ? filter.order === 'asc'
                  ? 'ascend'
                  : 'descend'
                : null,
            render: (_v, u) =>
              (filter.periodId ? (u.phasePoints ?? 0) : u.totalPoints).toLocaleString('it-IT'),
          },
          {
            title: 'Affidabilità',
            dataIndex: 'trustScore',
            key: 'trustScore',
            width: 130,
            sorter: true,
            sortOrder:
              filter.sortBy === 'trustScore'
                ? filter.order === 'asc'
                  ? 'ascend'
                  : 'descend'
                : null,
            render: (v: number, u) => (
              <Space direction="vertical" size={0}>
                <Tag color={trustScoreColor(v)} style={{ fontFamily: 'monospace' }}>
                  {v}/100
                </Tag>
                {u.trustScoreUpdatedAt ? (
                  <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                    aggiornato {dayjs(u.trustScoreUpdatedAt).format('DD/MM HH:mm')}
                  </Typography.Text>
                ) : (
                  <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                    mai calcolato
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          {
            title: 'Stato',
            width: 120,
            render: (_v, u) => {
              if (u.isBanned) return <Tag color="red">Bannato</Tag>;
              if (!u.isActive) return <Tag>Disattivo</Tag>;
              return <Tag color="green">Attivo</Tag>;
            },
          },
          {
            title: 'Iscritto',
            dataIndex: 'createdAt',
            width: 110,
            responsive: ['lg'],
            render: (v: string) => new Date(v).toLocaleDateString('it-IT'),
          },
          {
            title: 'Azioni',
            width: 150,
            align: 'right',
            render: (_v, u) => (
              <FanRowActions
                user={u}
                onEdit={openEdit}
                onPoints={(x) => {
                  pointsForm.resetFields();
                  setPointsUser(x);
                }}
                onReceipt={(x) => {
                  receiptForm.resetFields();
                  receiptForm.setFieldsValue({ pointsAwarded: 10, date: dayjs() });
                  setReceiptUser(x);
                }}
                onLoyalty={(x) => {
                  loyaltyForm.resetFields();
                  loyaltyForm.setFieldsValue({ kind: 'SUBSCRIPTION', season: '2026/27' });
                  setLoyaltyUser(x);
                }}
                onBan={(x) => {
                  banForm.resetFields();
                  setBanUser(x);
                }}
                recalcOne={(id) => recalcOneMutation.mutateAsync(id)}
                recalcPending={recalcOneMutation.isPending}
                onErr={handleErr}
              />
            ),
          },
        ]}
      />

      {/* Ban modal */}
      <Modal
        title={`Banna utente: ${banUser?.displayName ?? banUser?.email}`}
        open={banUser !== null}
        onCancel={() => setBanUser(null)}
        onOk={onSubmitBan}
        okText="Banna"
        okButtonProps={{ danger: true }}
        confirmLoading={banMutation.isPending}
        destroyOnHidden
      >
        <Form form={banForm} layout="vertical" preserve={false}>
          <Form.Item
            name="reason"
            label="Motivo del ban"
            rules={[{ required: true, min: 3, max: 500 }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="es. Violazione termini di servizio, comportamento abusivo, ..."
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit user modal */}
      <Modal
        title={`Modifica tifoso: ${editUser?.displayName ?? editUser?.email}`}
        open={editUser !== null}
        onCancel={() => setEditUser(null)}
        onOk={onSubmitEdit}
        okText="Salva"
        confirmLoading={updateMutation.isPending}
        destroyOnHidden
        width={620}
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="firstName" label="Nome">
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Cognome">
                <Input maxLength={100} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="locale" label="Locale">
                <Select
                  options={[
                    { value: 'it', label: 'Italiano' },
                    { value: 'en', label: 'English' },
                  ]}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="role" label="Ruolo">
                <Select
                  options={Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label }))}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Loyalty award modal (abbonamento / biglietto) */}
      <Modal
        title={`Punti loyalty: ${loyaltyUser?.displayName ?? loyaltyUser?.email}`}
        open={loyaltyUser !== null}
        onCancel={() => setLoyaltyUser(null)}
        onOk={onSubmitLoyalty}
        okText="Accredita"
        confirmLoading={awardSubscriptionMutation.isPending || awardTicketMutation.isPending}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          Accredito manuale dei punti previsti dal regolamento §5d. L&apos;operazione è idempotente
          (lo stesso abbonamento/biglietto non viene accreditato due volte).
        </Typography.Paragraph>
        <Form form={loyaltyForm} layout="vertical" preserve={false}>
          <Form.Item name="kind" label="Tipo" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'SUBSCRIPTION', label: 'Abbonamento (per settore)' },
                { value: 'TICKET', label: 'Biglietto singola gara (+20 pt)' },
              ]}
            />
          </Form.Item>
          {loyaltyKind === 'SUBSCRIPTION' && (
            <>
              <Form.Item name="tier" label="Settore abbonamento" rules={[{ required: true }]}>
                <Select
                  options={Object.entries(SUBSCRIPTION_TIER_LABELS).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              </Form.Item>
              <Form.Item name="season" label="Stagione" rules={[{ required: true, max: 20 }]}>
                <Input placeholder="2026/27" />
              </Form.Item>
            </>
          )}
          {loyaltyKind === 'TICKET' && (
            <Form.Item name="note" label="Nota (facoltativa)">
              <Input placeholder="es. Biglietto Ciaotickets gara vs ..." maxLength={255} />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Points adjust modal */}
      <Modal
        title={`Aggiusta punti: ${pointsUser?.displayName ?? pointsUser?.email}`}
        open={pointsUser !== null}
        onCancel={() => setPointsUser(null)}
        onOk={onSubmitPoints}
        okText="Applica"
        confirmLoading={pointsMutation.isPending}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          Punti attuali:{' '}
          <Typography.Text strong>
            {pointsUser?.totalPoints.toLocaleString('it-IT') ?? 0}
          </Typography.Text>
          . Inserisci delta positivo per aggiungere, negativo per togliere.
        </Typography.Paragraph>
        <Form form={pointsForm} layout="vertical" preserve={false}>
          <Form.Item
            name="delta"
            label="Delta punti (es. +50 o -20)"
            rules={[{ required: true, type: 'number', min: -10000, max: 10000 }]}
          >
            <InputNumber min={-10000} max={10000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Motivo (audit log)"
            rules={[{ required: true, min: 3, max: 500 }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="es. Compensazione errore approvazione, premio fedeltà, correzione duplicato..."
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Manual receipt modal */}
      <Modal
        title={`Scontrino manuale: ${receiptUser?.displayName ?? receiptUser?.email}`}
        open={receiptUser !== null}
        onCancel={() => setReceiptUser(null)}
        onOk={onSubmitReceipt}
        okText="Crea scontrino"
        confirmLoading={manualReceiptMutation.isPending}
        destroyOnHidden
        width={680}
      >
        <Typography.Paragraph type="secondary">
          Il tifoso non ha caricato l&apos;immagine. Inserisci dati noti, status sarà APPROVED automatico.
        </Typography.Paragraph>
        <Form form={receiptForm} layout="vertical" preserve={false}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="pointsAwarded"
                label="Punti da assegnare"
                rules={[{ required: true, type: 'number', min: 0, max: 10000 }]}
              >
                <InputNumber min={0} max={10000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="totalAmount" label="Importo (EUR)">
                <InputNumber
                  min={0}
                  max={100000}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="storeName" label="Negozio">
                <Input maxLength={255} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="partitaIva" label="P.IVA">
                <Input maxLength={20} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="businessName" label="Ragione sociale">
            <Input maxLength={255} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="date" label="Data scontrino">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="receiptNumber" label="Numero scontrino">
                <Input maxLength={50} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="note" label="Note (audit log)">
            <Input.TextArea
              rows={2}
              placeholder="es. Scontrino smarrito dal tifoso, foto inviata via WhatsApp..."
              maxLength={500}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

// Stile condiviso delle voci del menu "Altre azioni": pulsanti a tutta
// larghezza, icona + etichetta allineate a sinistra come in un menu a tendina.
const menuItemStyle: React.CSSProperties = { justifyContent: 'flex-start', textAlign: 'left' };

// Ombra del pannello fluttuante: la stessa dei menu AntD (popupRender sostituisce
// il markup del menu, quindi lo stile va fornito noi).
const MENU_PANEL_SHADOW =
  '0 6px 16px 0 rgba(0,0,0,0.08), 0 3px 6px -4px rgba(0,0,0,0.12), 0 9px 28px 8px rgba(0,0,0,0.05)';

/**
 * T-212 — azioni per riga in /fans. Le due azioni più usate (modifica dati e
 * aggiusta punti) restano in riga; tutte le altre — scontrino, loyalty,
 * ricalcolo trust, reset/imposta password, ban, eliminazione — vivono in un
 * menu a tendina "tre puntini", così la colonna non richiede più scorrimento
 * orizzontale. Le voci con logica propria (reset/imposta password, elimina)
 * riusano i componenti esistenti in `menuMode` per non duplicarne le regole.
 */
function FanRowActions({
  user,
  onEdit,
  onPoints,
  onReceipt,
  onLoyalty,
  onBan,
  recalcOne,
  recalcPending,
  onErr,
}: {
  user: UserListItemDto;
  onEdit: (u: UserListItemDto) => void;
  onPoints: (u: UserListItemDto) => void;
  onReceipt: (u: UserListItemDto) => void;
  onLoyalty: (u: UserListItemDto) => void;
  onBan: (u: UserListItemDto) => void;
  recalcOne: (id: string) => Promise<{ score: number }>;
  recalcPending: boolean;
  onErr: (e: unknown) => string;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const close = (): void => setOpen(false);

  return (
    <Space size={4}>
      <Button
        size="small"
        icon={<EditOutlined />}
        onClick={() => onEdit(user)}
        title="Modifica dati"
      />
      <Button
        size="small"
        icon={<StarOutlined />}
        onClick={() => onPoints(user)}
        title="Aggiusta punti"
      >
        Punti
      </Button>
      <Dropdown
        open={open}
        onOpenChange={setOpen}
        trigger={['click']}
        placement="bottomRight"
        popupRender={() => (
          <div
            role="menu"
            style={{
              minWidth: 210,
              padding: 4,
              background: 'var(--ant-color-bg-elevated, #fff)',
              borderRadius: 8,
              boxShadow: MENU_PANEL_SHADOW,
            }}
          >
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Button
                block
                type="text"
                style={menuItemStyle}
                icon={<FileImageOutlined />}
                onClick={() => {
                  close();
                  onReceipt(user);
                }}
              >
                Scontrino manuale
              </Button>
              <Button
                block
                type="text"
                style={menuItemStyle}
                icon={<TrophyOutlined />}
                onClick={() => {
                  close();
                  onLoyalty(user);
                }}
                title="Accredita punti abbonamento / biglietto (regolamento §5d)"
              >
                Loyalty
              </Button>
              <Button
                block
                type="text"
                style={menuItemStyle}
                icon={<SafetyCertificateOutlined />}
                loading={recalcPending}
                onClick={async () => {
                  try {
                    const r = await recalcOne(user.id);
                    message.success(`Trust score: ${r.score}/100`);
                  } catch (e) {
                    message.error(onErr(e));
                  }
                  close();
                }}
                title="Ricalcola trust score (forza ora, non aspetta cron)"
              >
                Ricalcola trust
              </Button>
              <PasswordResetButton userId={user.id} menuMode onActivate={close} />
              <SetPasswordButton userId={user.id} menuMode onActivate={close} />
              {!user.isBanned ? (
                <Button
                  block
                  danger
                  type="text"
                  style={menuItemStyle}
                  icon={<StopOutlined />}
                  onClick={() => {
                    close();
                    onBan(user);
                  }}
                >
                  Banna
                </Button>
              ) : null}
              <DeleteFanButton user={user} menuMode onActivate={close} />
            </Space>
          </div>
        )}
      >
        <Button size="small" icon={<MoreOutlined />} title="Altre azioni" />
      </Dropdown>
    </Space>
  );
}

/**
 * Bottone invio email reset password. Disabilitato (con tooltip) se l'utente
 * è SSO-only — Firebase non ha la password da resettare. Modal di conferma
 * + audit lato backend.
 */
function PasswordResetButton({
  userId,
  menuMode,
  onActivate,
}: {
  userId: string;
  menuMode?: boolean;
  onActivate?: () => void;
}): React.ReactElement {
  const { data: providers, isLoading: providersLoading } = useUserProviders(userId);
  const sendReset = useAdminSendPasswordReset();

  const canReset = providers?.canResetPassword !== false;
  const reason =
    providers && !canReset
      ? `Utente SSO-only (${providers.providers.join(', ')})`
      : 'Invia email reset password';

  const onClick = () => {
    onActivate?.();
    Modal.confirm({
      title: 'Inviare email reset password?',
      content:
        'L\'utente riceverà una mail con un link per impostare una nuova password. L\'azione è registrata nel log di audit.',
      okText: 'Invia email',
      okButtonProps: { type: 'primary' },
      onOk: async () => {
        try {
          await sendReset.mutateAsync(userId);
          message.success('Email reset inviata');
        } catch (err) {
          const m = err instanceof Error ? err.message : String(err);
          message.error(`Invio fallito: ${m}`);
        }
      },
    });
  };

  const disabled = !canReset || providersLoading || sendReset.isPending;

  return menuMode ? (
    <Button
      block
      type="text"
      style={menuItemStyle}
      icon={<MailOutlined />}
      onClick={onClick}
      disabled={disabled}
      loading={sendReset.isPending}
      title={reason}
    >
      Reset password
    </Button>
  ) : (
    <Button
      size="small"
      icon={<MailOutlined />}
      onClick={onClick}
      disabled={disabled}
      loading={sendReset.isPending}
      title={reason}
    />
  );
}

/**
 * T-154 — imposta direttamente una nuova password per un tifoso email+password.
 * Il pulsante è DISABILITATO per gli utenti SSO-only (Google/Apple), con il
 * motivo scritto nel tooltip: la password la gestisce il provider. La stessa
 * regola è applicata (e non solo qui) lato server, che rifiuta 409 gli SSO-only.
 * La password non viene mai mostrata dopo l'invio né persistita lato client.
 */
export function SetPasswordButton({
  userId,
  menuMode,
  onActivate,
}: {
  userId: string;
  menuMode?: boolean;
  onActivate?: () => void;
}): React.ReactElement {
  const { data: providers, isLoading: providersLoading } = useUserProviders(userId);
  const setPassword = useAdminSetPassword();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<{ password: string }>();

  const canSet = providers?.canResetPassword !== false;
  const reason =
    providers && !canSet
      ? `Utente SSO-only (${providers.providers.join(', ')}): la password la gestisce il provider`
      : 'Imposta una nuova password';

  const onSubmit = async () => {
    const { password } = await form.validateFields();
    try {
      await setPassword.mutateAsync({ userId, password });
      message.success('Password impostata');
      form.resetFields();
      setOpen(false);
    } catch (err) {
      const m = err instanceof Error ? err.message : String(err);
      message.error(`Impostazione fallita: ${m}`);
    }
  };

  const openModal = (): void => {
    onActivate?.();
    form.resetFields();
    setOpen(true);
  };

  return (
    <>
      {menuMode ? (
        <Button
          block
          type="text"
          style={menuItemStyle}
          icon={<KeyOutlined />}
          onClick={openModal}
          disabled={!canSet || providersLoading}
          title={reason}
        >
          Imposta password
        </Button>
      ) : (
        <Button
          size="small"
          icon={<KeyOutlined />}
          onClick={openModal}
          disabled={!canSet || providersLoading}
          title={reason}
        />
      )}
      <Modal
        open={open}
        title="Imposta nuova password"
        okText="Imposta password"
        okButtonProps={{ danger: true }}
        confirmLoading={setPassword.isPending}
        onOk={onSubmit}
        onCancel={() => {
          form.resetFields();
          setOpen(false);
        }}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          Impostando una password la <b>conoscerai</b> e potresti accedere come questo tifoso.
          L&apos;azione è registrata nel log di audit (chi, quando, su chi), senza il valore.
        </Typography.Paragraph>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="password"
            label="Nuova password"
            rules={[
              { required: true, message: 'Inserisci una password' },
              { min: 8, max: 128, message: 'Deve essere lunga tra 8 e 128 caratteri' },
              {
                pattern: /^(?=.*[A-Za-z])(?=.*\d).+$/,
                message: 'Deve contenere almeno una lettera e una cifra',
              },
            ]}
          >
            <Input.Password
              autoComplete="new-password"
              placeholder="Almeno 8 caratteri, lettere e cifre"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

/**
 * T-154 — elimina un tifoso. La conferma dice cosa succede DAVVERO (dati
 * personali rimossi, accesso disabilitato, ordini e punti conservati, uscita
 * dalla classifica) ed è irreversibile — non un generico "sei sicuro?".
 */
export function DeleteFanButton({
  user,
  menuMode,
  onActivate,
}: {
  user: UserListItemDto;
  menuMode?: boolean;
  onActivate?: () => void;
}): React.ReactElement {
  const del = useDeleteUser();
  const [open, setOpen] = useState(false);

  const onConfirm = async () => {
    try {
      const res = await del.mutateAsync(user.id);
      message.success(
        res.mode === 'hard'
          ? 'Tifoso eliminato (account senza storico, rimosso del tutto)'
          : 'Tifoso eliminato (dati anonimizzati, storico conservato)',
      );
      setOpen(false);
    } catch (err) {
      const m = err instanceof Error ? err.message : String(err);
      message.error(`Eliminazione fallita: ${m}`);
    }
  };

  const openModal = (): void => {
    onActivate?.();
    setOpen(true);
  };

  return (
    <>
      {menuMode ? (
        <Button
          block
          danger
          type="text"
          style={menuItemStyle}
          icon={<DeleteOutlined />}
          onClick={openModal}
          loading={del.isPending}
          title="Elimina tifoso"
        >
          Elimina
        </Button>
      ) : (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={openModal}
          loading={del.isPending}
          title="Elimina tifoso"
        />
      )}
      <Modal
        open={open}
        title="Eliminare questo tifoso?"
        okText="Elimina definitivamente"
        okButtonProps={{ danger: true }}
        cancelText="Annulla"
        confirmLoading={del.isPending}
        onOk={onConfirm}
        onCancel={() => setOpen(false)}
        width={540}
        destroyOnHidden
      >
        <Typography.Paragraph style={{ marginBottom: 8 }}>
          Stai per eliminare <b>{user.displayName || user.email}</b>. L&apos;azione è{' '}
          <b>irreversibile</b>. Ecco cosa succede:
        </Typography.Paragraph>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          <li>i suoi dati personali vengono rimossi o resi irriconoscibili;</li>
          <li>l&apos;accesso all&apos;app viene disabilitato: non potrà più entrare;</li>
          <li>
            i suoi ordini, scontrini e punti <b>restano</b> (la contabilità e le statistiche non
            cambiano);
          </li>
          <li>esce dalla classifica: chi era sotto di lui sale di una posizione;</li>
          <li>se non ha alcuno storico, l&apos;account viene cancellato del tutto.</li>
        </ul>
      </Modal>
    </>
  );
}
